import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Button, Pagination, Skeleton, Snackbar } from "@mui/material";
import DeleteModal from "@/components/DeleteModal/DeleteModal";
import EditMusicModal from "../EditMusicModal/EditMusicModal";
import AddMusicModal from "../AddMusicModal/AddMusicModal";
import searchIcon from "@/assets/icons/search.svg";
import { InputText } from "@/components/InputText/InputText";
import styles from "./AudioContent.module.scss";
import useItemsPerPage from "@/hooks/useItemsPerPage";
import { AudioCard } from "@/components/AudioCard/AudioCard";
import {
  useDeleteMediaMutation,
  useGetMediaMusicQuery,
  useUpdateMediaMutation,
} from "@/store/rtk-api/media.api";
import {
  useGetJobsPendingQuery,
  useUploadAudioMutation,
} from "@/store/rtk-api/media-upload.api";
import { IMedia } from "@/store/models/video.models";
import PublishModal from "@/components/PublishModal/PublishModal";

const AudioContent = () => {
  const [page, setPage] = useState(1);
  const [isOpenEditModal, setIsOpenEditModal] = useState(false);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [isOpenPublishModal, setIsOpenPublishModal] = useState(false);
  const [selectedMusic, setSelectedMusic] = useState<IMedia | null>(null);
  const [jobUid, setJobUid] = useState<number | null>(null);
  const [musicsQuantity, setMusicsQuantity] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [isOpenAddItemModal, setIsOpenAddItemModal] = useState(false);

  const itemsPerPage = useItemsPerPage(2, 4, 6, 1164, 1680);
  const { control, watch } = useForm();
  const searchInput = watch("search");

  const {
    data: musicData,
    refetch: refetchMusic,
    isLoading,
    isSuccess,
  } = useGetMediaMusicQuery({
    page: page,
    limit: itemsPerPage,
    searchTerm: searchInput,
    sortByDate: "DESC",
    isPublished: "none",
  });

  const { data: jobsPending, refetch: refetchJobsPending } =
    useGetJobsPendingQuery();

  const [deleteMusicByUid] = useDeleteMediaMutation();
  const [editMusic] = useUpdateMediaMutation();
  const [createMusic] = useUploadAudioMutation();

  useEffect(() => {
    if (jobsPending?.length > 0) {
      const intervalId = setInterval(() => {
        refetchJobsPending();
      }, 20000);

      return () => clearInterval(intervalId);
    } else if (jobsPending?.length === 0) {
      refetchMusic();
      setJobUid(null);
    }
  }, [jobsPending, refetchJobsPending, refetchMusic]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const handleToggleEditModal = (music: IMedia) => {
    setSelectedMusic(music);
    setIsOpenEditModal(true);
  };

  const handleToggleDeleteModal = (music: IMedia) => {
    setSelectedMusic(music);
    setIsOpenDeleteModal(true);
  };

  const handleTogglePublishModal = (video: IMedia) => {
    setSelectedMusic(video);
    setIsOpenPublishModal(true);
  };

  const handlePublishMusic = async () => {
    if (selectedMusic) {
      const formData = new FormData();
      formData.append("uid", selectedMusic.uid);
      if (selectedMusic.isPublished !== undefined) {
        formData.append("isPublished", String(!selectedMusic.isPublished));
      }

      await editMusic(formData);
      setIsOpenPublishModal(false);
      refetchMusic();
    }
  };

  const handleDeleteMusic = async () => {
    if (selectedMusic) {
      await deleteMusicByUid(selectedMusic.uid);
      setIsOpenDeleteModal(false);
      refetchMusic();
    }
  };

  const handleEditMusic = async (formData: FormData) => {
    await editMusic(formData);
    setIsOpenEditModal(false);
    refetchMusic();
  };

  const handleAddMusic = async (formData: FormData) => {
    try {
      const response = await createMusic(formData).unwrap();
      setJobUid(response.id);
    } catch (err) {
      setError("Failed to upload video. Please try again.");
      setSnackbarOpen(true);
    }
  };

  const handleToggleModal = () => {
    setIsOpenAddItemModal((prev) => !prev);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPage(1);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setError(null);
  };

  useEffect(() => {
    let singleMusic = 0;
    let multiplyMusic = 0;
    if (jobsPending?.length > 0) {
      jobsPending.forEach((job: any) => {
        if (job.name === "single-music-upload") {
          singleMusic += job.progress.total;
        } else if (job.name === "multiple-media-upload") {
          multiplyMusic += job.data.musics.length;
        }
      });
      setMusicsQuantity(singleMusic + multiplyMusic);
    }
  }, [jobsPending]);

  return (
    <div className={styles.container}>
      <div className={styles.topSection}>
        <Button
          sx={{
            textTransform: "capitalize",
            height: "44px",
            width: "174px",
            backgroundColor: "#008000",
            borderRadius: "12px",
            "&:hover": {
              backgroundColor: "#008000",
            },
          }}
          variant="contained"
          onClick={handleToggleModal}
        >
          Add item
        </Button>
        <InputText
          label="Search"
          control={control}
          name="search"
          icon={searchIcon}
          onChange={handleSearchChange}
        />
      </div>

      <div className={styles.content}>
        <div className={styles.itemsContainer}>
          {(jobsPending?.length > 0 || jobUid !== null) && (
            <AudioCard
              audio={{
                title: `${musicsQuantity} file(s) are being uploaded...`,
                description: "",
                uid: Math.random().toString(),
                url: "",
                accessType: "",
                isFavourite: false,
                isPublished: false,
                likes: 0,
                subtitleUrl: "",
                thumbnailPrompt: "",
                thumbnailUrl: "",
                type: "",
                viewsCount: 0,
                sections: [],
              }}
              isLoading={true}
              isEdit={false}
            />
          )}
          {isLoading &&
            Array(4)
              .fill(0)
              .map((_, index) => (
                <div className={styles.item} key={index}>
                  <div className={styles.itemInfoWrap}>
                    <div className={styles.itemInfo}>
                      <Skeleton
                        variant="text"
                        width={100}
                        animation="wave"
                        sx={{ marginBottom: 1 }}
                      />
                      <Skeleton
                        variant="text"
                        width={150}
                        animation="wave"
                        sx={{ marginBottom: 1 }}
                      />
                    </div>
                  </div>
                  <div className={styles.itemBtns}>
                    <Skeleton
                      variant="circular"
                      width={40}
                      height={40}
                      animation="wave"
                      sx={{ marginRight: 1 }}
                    />
                    <Skeleton
                      variant="circular"
                      width={40}
                      height={40}
                      animation="wave"
                    />
                  </div>
                </div>
              ))}
          {isSuccess && musicData.total > 0 ? (
            musicData.media.map((audio) => (
              <AudioCard
                key={audio.uid}
                audio={audio}
                handleToggleEditModal={handleToggleEditModal}
                handleToggleDeleteModal={handleToggleDeleteModal}
                handleTogglePublishModal={handleTogglePublishModal}
                isEdit={true}
              />
            ))
          ) : (
            <div className={styles.noDataText}>No data yet</div>
          )}
        </div>
        {isOpenEditModal && (
          <EditMusicModal
            isOpenEditModal={isOpenEditModal}
            setIsOpenEditModal={setIsOpenEditModal}
            selectedMusic={selectedMusic}
            setSelectedMusic={setSelectedMusic}
            handleEditMusic={handleEditMusic}
          />
        )}
        {isOpenDeleteModal && (
          <DeleteModal
            isOpen={isOpenDeleteModal}
            setIsOpen={setIsOpenDeleteModal}
            name={selectedMusic ? selectedMusic?.title : ""}
            supportingText="Are you sure you want to delete this audio?"
            onDelete={handleDeleteMusic}
          />
        )}

        {isOpenPublishModal && (
          <PublishModal
            isOpen={isOpenPublishModal}
            setIsOpen={setIsOpenPublishModal}
            name={selectedMusic ? selectedMusic.title : ""}
            isPublished={selectedMusic?.isPublished ?? true}
            onHandle={handlePublishMusic}
          />
        )}

        {isOpenAddItemModal && (
          <AddMusicModal
            isOpenAddItemModal={isOpenAddItemModal}
            setIsOpenAddItemModal={setIsOpenAddItemModal}
            onAddAudio={handleAddMusic}
          />
        )}
        {isSuccess && (
          <Pagination
            page={page}
            onChange={handlePageChange}
            count={Math.ceil(musicData.total / itemsPerPage)}
          />
        )}
      </div>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={error}
      />
    </div>
  );
};

export default AudioContent;
